/**
 * @fileoverview System-web inspection template editor and shared canvas host.
 * @module system-web/inspection-templates
 *
 * Responsibilities:
 * - Load and edit system-scoped inspection templates from local-first services.
 * - Host the shared ui-admin TemplateCanvas with system-web service adapters.
 * - Persist canvas form and module changes through system Dexie-backed services.
 *
 * Dependencies:
 * - systemInspectionTemplateService
 * - systemFormService
 * - system violation code set services
 * - @firesquirrel/ui-admin TemplateCanvas
 *
 * Architecture:
 * - UI callbacks write to local-first services; the shared canvas never calls API routes directly.
 * - System-web injects local Dexie-backed code set services so module UI matches tenant-web behavior.
 *
 * Notes:
 * - Keep canvas props in parity with tenant-web TemplateDetailPage when adding shared features.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Navigate, useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, Save, CheckCircle } from 'lucide-react';
import {
  getSystemCanvasTemplate,
  updateSystemCanvasTemplate,
  publishSystemInspectionTemplate,
} from '../services/systemInspectionTemplateService';
import type { TemplateBuilderTemplate } from '../services/systemInspectionTemplateService';
import * as systemFormService from '../services/systemFormService';
import type { TemplateBuilderForm, FormRef, CanvasFormApiService, TemplateType, ViolationCodeSet, ViolationIndexEntry } from '@firesquirrel/ui-admin';
import { InspectionTemplateEditorShell, FormLibraryPicker, TemplateAiMetadataSection, TemplateGeneralTab, TemplateLibrarySettingsSection, TemplateSyncStatePill, useTemplateCanvasCallbacks, useTemplateDirtyGuard } from '@firesquirrel/ui-admin';
import { SystemTemplatePreviewMode } from '../components/template-builder/SystemTemplatePreviewMode';
// Section 2.8.6: Violations tab picker needs the system Dexie instance.
import { getSystemDb } from '@firesquirrel/local-db';
import { listSystemViolationCodeSetArtifacts } from '../services/systemViolationCodeSetLibraryService';

// ---------------------------------------------------------------------------
// Tab type
// ---------------------------------------------------------------------------
type Tab = 'general' | 'canvas';

const TABS: { id: Tab; label: string }[] = [
  { id: 'general', label: 'General' },
  { id: 'canvas',  label: 'Canvas' },
];

/**
 * @localFirst
 *
 * Source of Truth:
 * - local
 *
 * Write Flow:
 * - Read-only adapter; system module selection does not mutate code set data.
 *
 * Sync Strategy:
 * - System sync pulls code set artifacts into Dexie before this adapter reads them.
 *
 * Conflict Resolution:
 * - Not applicable for this read-only projection.
 */
const systemCanvasViolationCodeSetService = {
  async listCodeSets(): Promise<ViolationCodeSet[]> {
    const artifacts = await listSystemViolationCodeSetArtifacts();
    const now = new Date().toISOString();
    return artifacts
      .filter((artifact) => artifact.status !== 'failed')
      .map((artifact) => ({
        _id: artifact._id,
        name: artifact.title,
        description: `${artifact.vendorLabel}${artifact.edition && artifact.edition !== '—' ? ` · ${artifact.edition}` : ''}`,
        source: artifact.vendorId === 'icc' || artifact.vendorId === 'nfpa' ? artifact.vendorId : 'custom',
        isActive: true,
        version: 1,
        createdAt: artifact.downloadedAt ?? artifact.updatedAt ?? now,
        createdBy: 'system',
        updatedAt: artifact.updatedAt ?? artifact.downloadedAt ?? now,
        updatedBy: 'system',
      }));
  },
  async searchViolations(_query: string, _codeSetId: string): Promise<ViolationIndexEntry[]> {
    return [];
  },
};

// ---------------------------------------------------------------------------
// Main Editor
// ---------------------------------------------------------------------------
export default function SystemInspectionTemplateEditorPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';

  const [loading, setLoading] = useState(!isNew);
  const [template, setTemplate] = useState<TemplateBuilderTemplate | null>(null);
  const [forms, setForms] = useState<Map<string, TemplateBuilderForm>>(new Map());
  const [activeTab, setActiveTab] = useState<Tab>('general');
  const [saveStatus, setSaveStatus] = useState<'saved' | 'saving' | 'error' | 'dirty'>('saved');
  const [saveError, setSaveError] = useState<string | null>(null);
  const dirtyGuard = useTemplateDirtyGuard({ isDirty: saveStatus === 'dirty' || saveStatus === 'saving' });
  const [libraryPickerOpen, setLibraryPickerOpen] = useState(false);
  const autoSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Section 2.8.6: lazily-resolved system Dexie instance for the Violations panel picker.
  const [systemDexieDb, setSystemDexieDb] = useState<any>(null);
  useEffect(() => {
    let cancelled = false;
    getSystemDb().then((db) => { if (!cancelled) setSystemDexieDb(db); }).catch((err) => {
      console.warn('[SystemInspectionTemplateEditorPage] Failed to load system DB:', err);
    });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (isNew) return;
    setLoading(true);
    getSystemCanvasTemplate(id!)
      .then((t) => {
        if (!t) { navigate('/inspection-templates'); return; }
        setTemplate(t);
      })
      .catch(() => setSaveError('Failed to load template.'))
      .finally(() => setLoading(false));
  }, [isNew, id, navigate]);

  const formRefsKey = (template?.formRefs ?? [])
    .map((ref) => `${ref.formId}:${ref.formVersion}:${ref.formKind ?? ''}`)
    .join('|');

  useEffect(() => {
    const currentTemplate = template;
    if (!currentTemplate?.formRefs?.length) { setForms(new Map()); return; }
    let cancelled = false;
    const refs = currentTemplate.formRefs;
    const formIds = refs.map((r) => r.formId);
    Promise.allSettled(
      formIds.map((id) => import('../services/systemFormService').then((m) => m.getFormById(id))),
    ).then((results) => {
      if (cancelled) return;
      const map = new Map<string, TemplateBuilderForm>();
      const missingForms: string[] = [];
      results.forEach((r, idx) => {
        if (r.status === 'fulfilled') {
          if (r.value) {
            // Form-load repair (2.1): if form.formKind is missing, recover it
            // from the matching template.formRefs[i] so downstream code that
            // branches on formKind (header/module/standard) doesn't silently
            // drop into the wrong path.
            const loaded = r.value;
            const matchingRef = refs.find((ref) => ref.formId === loaded._id);
            if (!loaded.formKind && matchingRef?.formKind) {
              map.set(loaded._id, { ...loaded, formKind: matchingRef.formKind } as TemplateBuilderForm);
            } else {
              map.set(loaded._id, loaded);
            }
          } else {
            missingForms.push(formIds[idx]);
          }
        } else {
          console.error(`Failed to load form ${formIds[idx]}:`, r.reason);
          missingForms.push(formIds[idx]);
        }
      });
      if (missingForms.length > 0) {
        console.warn(`Template references ${missingForms.length} missing form(s):`, missingForms);
      }
      setForms(map);
    });
    return () => { cancelled = true; };
  }, [template?._id, formRefsKey]);


  const scheduleSave = useCallback((tmpl: TemplateBuilderTemplate) => {
    setSaveStatus('dirty');
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(async () => {
      setSaveStatus('saving');
      try {
        const saved = await updateSystemCanvasTemplate(tmpl._id, tmpl);
        setTemplate(saved);
        setSaveError(null);
        setSaveStatus('saved');
      } catch (err) {
        setSaveError(err instanceof Error ? err.message : 'Save failed.');
        setSaveStatus('error');
      }
    }, 1500);
  }, []);

  const patchTemplate = useCallback((patch: Partial<TemplateBuilderTemplate>) => {
    setTemplate((prev) => {
      if (!prev) return prev;
      const next = { ...prev, ...patch };
      scheduleSave(next);
      return next;
    });
  }, [scheduleSave]);

  const handlePublish = useCallback(async () => {
    if (!template) return;
    if (!template.formRefs || template.formRefs.length === 0) {
      setSaveError('Cannot publish an empty template. Add at least one form before publishing.');
      setSaveStatus('error');
      return;
    }
    setSaveStatus('saving');
    try {
      if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
      await updateSystemCanvasTemplate(template._id, template);
      const published = await publishSystemInspectionTemplate(template._id);
      setTemplate((prev) => prev ? { ...prev, status: published.status as any, version: published.version } : prev);
      setSaveStatus('saved');
    } catch (err: any) {
      setSaveError(err.message ?? 'Publish failed.');
      setSaveStatus('error');
    }
  }, [template]);

  const formApiService: CanvasFormApiService = {
    toggleFormFavorite: async (formId) => {
      const updated = await systemFormService.toggleFormFavorite(formId);
      if (updated) setForms((prev) => new Map(prev).set(formId, updated));
      return updated;
    },
    updateForm: async (formId, patch) => {
      const updated = await systemFormService.updateForm(formId, patch);
      setForms((prev) => new Map(prev).set(formId, updated));
      return updated;
    },
    propagateLibraryUpdate: async () => 0,
    createForm: async (data) => {
      const created = await systemFormService.createForm(data);
      setForms((prev) => new Map(prev).set(created._id, created));
      return created;
    },
  };

  // ---------------------------------------------------------------------------
  // Shared canvas mutation callbacks (single reusable implementation from
  // ui-admin; tenant-web uses the same hook — no duplication between apps).
  // ---------------------------------------------------------------------------
  const {
    onNewFormAdded,
    onAddModuleForm,
    onRemoveFormRef,
    onReorderForms,
    onUpgradeFormRef,
  } = useTemplateCanvasCallbacks({
    // Hook is called unconditionally (React rules); null-safe fallback is never
    // used in practice because the shell only renders when template is non-null.
    template: template ?? ({ formRefs: [] } as unknown as TemplateBuilderTemplate),
    patchTemplate,
    setForms,
  });

  if (isNew) {
    return <Navigate to="/inspection-templates" replace />;
  }

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center text-sm text-gray-400">
        Loading template…
      </div>
    );
  }

  // A stale/deleted template link should resolve to the library rather than
  // leaving an empty editor route mounted while the imperative load effect
  // settles.
  if (!template) return <Navigate to="/inspection-templates" replace />;

  const isDraft = template.status === 'draft';

  return (
    <>
      <InspectionTemplateEditorShell
        tabs={TABS}
        activeTab={activeTab}
        onTabChange={(id) => setActiveTab(id as Tab)}
        allowDeferViolations={true}
        headerSlot={
          <div className="px-4 py-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <button
                onClick={() => { if (dirtyGuard.confirmNavigation()) navigate('/inspection-templates'); }}
                className="p-1.5 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
              >
                <ChevronLeft size={18} />
              </button>
              <div className="min-w-0">
                <h1 className="text-sm font-semibold text-gray-900 truncate">{template.name}</h1>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
                    template.status === 'published' ? 'bg-green-100 text-green-700' :
                    template.status === 'archived'  ? 'bg-gray-100 text-gray-600' :
                    'bg-amber-100 text-amber-700'
                  }`}>{template.status}</span>
                  <TemplateSyncStatePill
                    status={saveStatus === 'dirty' ? 'pending' : saveStatus === 'saving' ? 'saving' : saveStatus === 'error' ? 'failed' : 'synced'}
                    error={saveError}
                    compact={false}
                  />
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {saveError && <span className="text-xs text-red-600 max-w-xs truncate">{saveError}</span>}
              <button
                onClick={() => scheduleSave(template)}
                disabled={saveStatus === 'saving' || !template.name.trim()}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
                title={!template.name.trim() ? 'Template name is required' : 'Save changes'}
              >
                <Save size={13} /> Save
              </button>
              {isDraft && (
                <button
                  onClick={handlePublish}
                  disabled={saveStatus === 'saving'}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white rounded-lg hover:opacity-90 disabled:opacity-50 transition-colors"
                  style={{ backgroundColor: 'var(--brand-action)' }}
                >
                  <CheckCircle size={13} /> Publish to Library
                </button>
              )}
              {!isDraft && (
                <div className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg">
                  <CheckCircle size={13} /> Published
                </div>
              )}
            </div>
          </div>
        }
        renderTab={(tabId) => {
          if (tabId === 'general') {
            return (
              <div className="flex-1 overflow-y-auto">
                <TemplateGeneralTab
                  data={template}
                  scope="system"
                  onSave={patchTemplate}
                  librarySlot={<TemplateLibrarySettingsSection template={template} onSave={patchTemplate} />}
                  aiMetadataSlot={<TemplateAiMetadataSection template={template} onSave={patchTemplate} />}
                />
              </div>
            );
          }
          return null;
        }}
        template={template}
        forms={forms}
        formApiService={formApiService}
        formListService={systemFormService}
        violationCodeSetService={systemCanvasViolationCodeSetService}
        onUpdateTemplate={patchTemplate}
        onUpdateForm={(formId, patch) => {
          let previousForm: TemplateBuilderForm | undefined;
          setForms((prev) => {
            const existing = prev.get(formId);
            if (!existing) return prev;
            previousForm = existing;
            return new Map(prev).set(formId, { ...existing, ...patch });
          });
          systemFormService.updateForm(formId, patch)
            .then((updated) => {
              setForms((prev) => new Map(prev).set(formId, updated));
              setSaveError(null);
            })
            .catch((err) => {
              console.error('[SystemInspectionTemplateEditorPage] Failed to persist canvas form update:', err);
              if (previousForm) {
                setForms((prev) => new Map(prev).set(formId, previousForm!));
              }
              setSaveError('Failed to save form changes.');
              setSaveStatus('error');
            });
        }}
        onNewFormAdded={onNewFormAdded}
        onAddForm={async (choice) => {
          if (choice === 'library') { setLibraryPickerOpen(true); return; }
          // Section 2.1: only 'header' reaches here — 'new' is handled inside TemplateCanvas.
          const created = await systemFormService.createForm({
            name: 'Header Form',
            formKind: 'header',
            templateType: template.templateType,
          });
          setForms((prev) => new Map(prev).set(created._id, created));
          const headerRef: FormRef = { formId: created._id, formVersion: 1, order: 0, formKind: 'header' };
          patchTemplate({ formRefs: [headerRef, ...template.formRefs.map((r, i) => ({ ...r, order: i + 1 }))] });
        }}
        onAddFormFromLibrary={(libraryForm) => {
          // Section 2.1: propagate formKind + moduleConfig for header/module forms from library.
          const newRef: FormRef = {
            formId: libraryForm._id,
            formVersion: libraryForm.version,
            order: template.formRefs.length,
            formKind: libraryForm.formKind,
            moduleConfig: libraryForm.moduleConfig,
          };
          setForms((prev) => new Map(prev).set(libraryForm._id, libraryForm));
          patchTemplate({ formRefs: [...template.formRefs, newRef] });
        }}
        onAddModuleForm={onAddModuleForm}
        onRemoveFormRef={onRemoveFormRef}
        onReorderForms={onReorderForms}
        onUpgradeFormRef={onUpgradeFormRef}
        previewRenderer={SystemTemplatePreviewMode}
        dexieDb={systemDexieDb}
      />
      {libraryPickerOpen && (
        <FormLibraryPicker
          templateType={template.templateType as TemplateType}
          formService={systemFormService}
          onSelect={(libraryForm) => {
            const newRef: FormRef = {
              formId: libraryForm._id,
              formVersion: libraryForm.version,
              order: template.formRefs.length,
            };
            setForms((prev) => new Map(prev).set(libraryForm._id, libraryForm));
            patchTemplate({ formRefs: [...template.formRefs, newRef] });
            setLibraryPickerOpen(false);
          }}
          onClose={() => setLibraryPickerOpen(false)}
        />
      )}
    </>
  );
}
